import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatApiError } from '../lib/format';

const SA_MOBILE = /^(\+27|0)[6-8][0-9]{8}$/;

const inputBase =
  'mt-1.5 w-full bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]';

export default function InlineAuthDialog({ open, onOpenChange, onSuccess }) {
  const { login } = useAuth();
  const [tab, setTab] = useState('login');

  // login state
  const [le, setLe] = useState('');
  const [lp, setLp] = useState('');
  const [lLoading, setLLoading] = useState(false);

  // signup state
  const [su, setSu] = useState({
    first_name: '',
    surname: '',
    mobile: '',
    age: '',
    email: '',
    password: '',
  });
  const [sLoading, setSLoading] = useState(false);

  const doLogin = async (e) => {
    e.preventDefault();
    setLLoading(true);
    try {
      const { data } = await api.post('/auth/login', {
        email: le.trim().toLowerCase(),
        password: lp,
      });
      login(data.token, data.user);
      toast.success('Welcome back');
      onSuccess?.(data.user, { justSignedUp: false });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLLoading(false);
    }
  };

  const doSignup = async (e) => {
    e.preventDefault();
    const ageNum = parseInt(su.age || '0', 10);
    if (isNaN(ageNum) || ageNum < 18) {
      toast.error('You must be 18 or older to sign up');
      return;
    }
    if (!SA_MOBILE.test(su.mobile.replace(/\s/g, ''))) {
      toast.error('Invalid SA mobile number');
      return;
    }
    if ((su.password || '').length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSLoading(true);
    try {
      const { data } = await api.post('/auth/signup', {
        first_name: su.first_name.trim(),
        surname: su.surname.trim(),
        mobile: su.mobile.replace(/\s/g, ''),
        age: ageNum,
        email: su.email.trim().toLowerCase(),
        password: su.password,
      });
      if (data.token && data.user) {
        login(data.token, data.user);
      }
      toast.success('Account created — welcome!');
      onSuccess?.(data.user, { justSignedUp: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSLoading(false);
    }
  };

  const setSuField = (k) => (e) => setSu({ ...su, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="inline-auth-dialog"
        className="bg-[#141414] border border-[#D4AF37]/30 text-white max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">
            <span className="text-gold-gradient">Continue to payment</span>
          </DialogTitle>
          <DialogDescription className="text-[#C0C0C0]">
            Log in or create an account to secure your tickets.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="grid grid-cols-2 bg-[#0a0a0a] border border-[#D4AF37]/25">
            <TabsTrigger
              value="login"
              data-testid="auth-tab-login"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-[#C0C0C0]"
            >
              Login
            </TabsTrigger>
            <TabsTrigger
              value="signup"
              data-testid="auth-tab-signup"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-[#C0C0C0]"
            >
              Quick Sign Up
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="mt-4">
            <form onSubmit={doLogin} className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#C0C0C0]/80">Email</label>
                <input
                  data-testid="inline-login-email"
                  type="email"
                  required
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={le}
                  onChange={(e) => setLe(e.target.value)}
                  className={inputBase}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#C0C0C0]/80">Password</label>
                <input
                  data-testid="inline-login-password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={lp}
                  onChange={(e) => setLp(e.target.value)}
                  className={inputBase}
                />
              </div>
              <button
                type="submit"
                data-testid="inline-login-submit"
                disabled={lLoading}
                className="btn-gold w-full inline-flex items-center justify-center gap-2 mt-2"
              >
                {lLoading && <Loader2 className="animate-spin" size={16} />}
                Log in &amp; continue
              </button>
            </form>
          </TabsContent>

          <TabsContent value="signup" className="mt-4">
            <form onSubmit={doSignup} className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#C0C0C0]/80">First name</label>
                <input data-testid="inline-signup-firstname" required value={su.first_name} onChange={setSuField('first_name')} className={inputBase} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#C0C0C0]/80">Surname</label>
                <input data-testid="inline-signup-surname" required value={su.surname} onChange={setSuField('surname')} className={inputBase} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#C0C0C0]/80">Mobile (SA)</label>
                <input data-testid="inline-signup-mobile" required placeholder="0821234567" value={su.mobile} onChange={setSuField('mobile')} className={inputBase} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#C0C0C0]/80">Age (18+)</label>
                <input data-testid="inline-signup-age" required type="number" min={18} max={120} value={su.age} onChange={setSuField('age')} className={inputBase} />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-[#C0C0C0]/80">Email</label>
                <input data-testid="inline-signup-email" required type="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={su.email} onChange={setSuField('email')} className={inputBase} />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-[#C0C0C0]/80">Password (min 8)</label>
                <input data-testid="inline-signup-password" required type="password" minLength={8} value={su.password} onChange={setSuField('password')} className={inputBase} />
              </div>
              <button
                type="submit"
                data-testid="inline-signup-submit"
                disabled={sLoading}
                className="btn-gold col-span-2 mt-2 inline-flex items-center justify-center gap-2"
              >
                {sLoading && <Loader2 className="animate-spin" size={16} />}
                Sign up &amp; continue
              </button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
